// Editor.js block tool for an embedded OpenStreetMap map. Stores
//   { lat, lon, zoom, label, directions }
// — numbers plus a display label; the live site builds the embed URL from the
// validated numbers only (see the template's renderMap). The search box
// geocodes via OSM Nominatim; lat/lon can also be typed in directly.

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>';

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const DEFAULT_ZOOM = 16;

function toCoord(value, max) {
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= max ? n : null;
}

export class MapTool {
  static get toolbox() {
    return { title: "Map", icon: ICON };
  }

  constructor({ data, block }) {
    this.block = block;
    this.data = {
      lat: toCoord(data?.lat, 90),
      lon: toCoord(data?.lon, 180),
      zoom: toCoord(data?.zoom, 19) ?? DEFAULT_ZOOM,
      label: typeof data?.label === "string" ? data.label : "",
      directions: data?.directions !== false,
    };
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-map-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-gallery-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-gallery-tool__title";
    title.textContent = "Map";

    const search = document.createElement("input");
    search.type = "text";
    search.className = "pgs-map-tool__search";
    search.placeholder = "Search an address…";
    search.setAttribute("aria-label", "Search an address");

    const searchBtn = document.createElement("button");
    searchBtn.type = "button";
    searchBtn.className = "pgs-gallery-tool__btn";
    searchBtn.textContent = "Find";
    const runSearch = () => this._search(search.value, searchBtn);
    searchBtn.addEventListener("click", runSearch);
    search.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        runSearch();
      }
    });

    bar.append(title, search, searchBtn);

    const body = document.createElement("div");
    body.className = "pgs-map-tool__body";

    this._status = document.createElement("p");
    this._status.className = "pgs-map-tool__status";

    this._preview = document.createElement("div");
    this._preview.className = "pgs-map-tool__preview";

    const fields = document.createElement("div");
    fields.className = "pgs-map-tool__fields";
    this._latInput = this._numberField(fields, "Latitude", this.data.lat, (v) => {
      this.data.lat = toCoord(v, 90);
      this._update();
    });
    this._lonInput = this._numberField(fields, "Longitude", this.data.lon, (v) => {
      this.data.lon = toCoord(v, 180);
      this._update();
    });
    this._zoomInput = this._numberField(fields, "Zoom (1–19)", this.data.zoom, (v) => {
      this.data.zoom = toCoord(v, 19) ?? DEFAULT_ZOOM;
      this._update();
    });

    const label = document.createElement("input");
    label.type = "text";
    label.className = "pgs-map-tool__label";
    label.placeholder = "Address line shown under the map (optional)";
    label.setAttribute("aria-label", "Address label");
    label.value = this.data.label;
    label.addEventListener("input", () => {
      this.data.label = label.value;
      this.block?.dispatchChange?.();
    });

    const directionsWrap = document.createElement("label");
    directionsWrap.className = "pgs-map-tool__directions";
    const directions = document.createElement("input");
    directions.type = "checkbox";
    directions.checked = this.data.directions;
    directions.addEventListener("change", () => {
      this.data.directions = directions.checked;
      this.block?.dispatchChange?.();
    });
    directionsWrap.append(directions, document.createTextNode(' Show a "Get directions" button'));

    body.append(this._preview, this._status, fields, label, directionsWrap);
    root.append(bar, body);

    this._update();
    return root;
  }

  _numberField(parent, labelText, value, onInput) {
    const wrap = document.createElement("label");
    wrap.className = "pgs-map-tool__field";
    const caption = document.createElement("span");
    caption.textContent = labelText;
    const input = document.createElement("input");
    input.type = "number";
    input.step = "any";
    input.value = value ?? "";
    input.addEventListener("input", () => {
      onInput(input.value);
      this.block?.dispatchChange?.();
    });
    wrap.append(caption, input);
    parent.appendChild(wrap);
    return input;
  }

  async _search(query, button) {
    const q = String(query || "").trim();
    if (!q) return;
    button.disabled = true;
    this._status.textContent = "Searching…";
    try {
      const res = await fetch(
        `${NOMINATIM_URL}?format=json&limit=1&q=${encodeURIComponent(q)}`,
        { headers: { accept: "application/json" } }
      );
      const results = await res.json();
      const hit = Array.isArray(results) ? results[0] : null;
      if (!hit) {
        this._status.textContent = "No results — try adding the town or postcode.";
        return;
      }
      this.data.lat = toCoord(hit.lat, 90);
      this.data.lon = toCoord(hit.lon, 180);
      this._latInput.value = this.data.lat ?? "";
      this._lonInput.value = this.data.lon ?? "";
      if (!this.data.label && typeof hit.display_name === "string") {
        this.data.label = hit.display_name.split(",").slice(0, 3).join(",").trim();
        const labelInput = this._preview.parentElement?.querySelector(".pgs-map-tool__label");
        if (labelInput) labelInput.value = this.data.label;
      }
      this._status.textContent = "";
      this._update();
      this.block?.dispatchChange?.();
    } catch {
      this._status.textContent = "Search failed — enter the coordinates manually.";
    } finally {
      button.disabled = false;
    }
  }

  _update() {
    const { lat, lon, zoom } = this.data;
    this._preview.innerHTML = "";
    if (lat === null || lon === null) {
      this._status.textContent = "Search for an address or enter coordinates to place the map.";
      return;
    }
    if (this._status.textContent.startsWith("Search for")) this._status.textContent = "";
    const dLon = 360 / Math.pow(2, Math.min(Math.max(Math.round(zoom ?? DEFAULT_ZOOM), 1), 19));
    const dLat = dLon * 0.4;
    const bbox = [
      Math.max(lon - dLon, -180), Math.max(lat - dLat, -90),
      Math.min(lon + dLon, 180), Math.min(lat + dLat, 90),
    ].map((n) => n.toFixed(6)).join("%2C");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)}%2C${lon.toFixed(6)}`;
    iframe.title = "Map preview";
    iframe.loading = "lazy";
    this._preview.appendChild(iframe);
  }

  save() {
    return {
      lat: this.data.lat,
      lon: this.data.lon,
      zoom: this.data.zoom,
      label: this.data.label.trim(),
      directions: this.data.directions,
    };
  }
}
